import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Loader2, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotUsername() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-username", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-5 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="mb-10 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E676]">
            <Zap className="h-5 w-5 text-black" fill="black" />
          </span>
          <span className="font-display text-xl font-black tracking-tight">TipNu</span>
        </Link>

        {sent ? (
          <div data-testid="username-sent">
            <div className="mb-2 flex items-center gap-2 text-[#00E676]">
              <MailCheck className="h-5 w-5" />
              <span className="text-sm font-semibold">Sent</span>
            </div>
            <h1 className="font-display text-4xl font-black tracking-tight">Check your email</h1>
            <p className="mt-2 text-zinc-400">
              If an account exists for <span className="text-white">{email}</span>, we've emailed your username.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl font-black tracking-tight">Forgot username?</h1>
            <p className="mt-2 text-zinc-400">Enter your email and we'll send you your username.</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-username-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Email</Label>
                <Input
                  data-testid="forgot-username-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              {error && <p data-testid="forgot-username-error" className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                data-testid="forgot-username-submit-btn"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center rounded-full bg-[#00E676] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Email me my username"}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-sm text-zinc-500">
          <Link to="/login" data-testid="forgot-username-to-login" className="font-semibold text-[#00E676]">Back to log in</Link>
        </p>
      </motion.div>
    </div>
  );
}
